import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, finalize, Subject, Subscription, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { NotificationsService, NotificationTypes } from '../../shared/services/notifications/notifications.service';
import { FilterService } from '@shared/services/filters/filter.service';
import { FiltersState } from '@shared/state/reducers/filters/filters.reducer';
import { selectFilters } from '@shared/state/selectors/filters/filters.selectors';
import { filterActions } from '@shared/state/actions/filters/filters.actions';
import { FilterItem } from '@shared/models/filters';
import { ListTypes } from '@shared/models/listing';
import { RoutingFilter } from '@addToEvent/add-to-event-ui';
import { Customer } from './interfaces/customer.interface';
import { CustomerService } from './services/customer.service';
import { GetCustomerParams } from './interfaces/get-customers-params.interface';

interface SortOption {
  label: string;
  value: string;
}

@Component({
    selector: 'customers-component',
    templateUrl: './customers.control.html',
    styleUrls: ['./customers.control.scss'],
    standalone: false
})
export class CustomersComponent implements OnInit, OnDestroy {
  customers: Customer[] = [];
  isLoading = false;
  searchTerm = '';
  innerWidth: number;
  filtersActive = false;
  filtersCounter = 0;
  customerFilters: FilterItem[] = [];
  selectedSort = 'updated,DESC';
  isArchivedFilter: boolean | undefined;
  readonly searchModelKey = 'title';

  public pagination: { page: number; pageCount: number };

  sortOptions: SortOption[] = [
    { label: 'Last Updated', value: 'updated,DESC' },
    { label: 'First Name', value: 'firstName,ASC' },
    { label: 'Last Name', value: 'lastName,ASC' }
  ];

  filterBarItems: RoutingFilter[] = [
    { name: 'All', url: '../all', icon: 'guests' },
    { name: 'Active', url: '../active', icon: 'tick-circle-v3' },
    { name: 'Archived', url: '../archived', icon: 'archive-v3' }
  ];

  private searchSubject = new Subject<string>();
  private searchSubscription: Subscription;
  private getCustomersSubscription: Subscription;
  private subFiltersState$: Subscription;
  private subscriptionList = new Subscription();

  constructor(
    private _customerService: CustomerService,
    private _notificationsService: NotificationsService,
    private _filterService: FilterService,
    private _filtersStore: Store<FiltersState>,
    private _router: Router,
    private _route: ActivatedRoute
  ) {}

  @HostListener('window:resize')
  onResize() {
    this.innerWidth = window.innerWidth;
  }

  ngOnInit() {
    this.innerWidth = window.innerWidth;

    const routeData = this._route.snapshot.data;
    this.isArchivedFilter = routeData['isArchived'];

    this._filterService.publishListType(ListTypes.CUSTOMERS);

    this.searchSubscription = this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(term => {
      this.searchTerm = term;
      this.updateSearchFilter(term);
    });

    this.subscribeToFiltersState();
  }

  ngOnDestroy() {
    this.getCustomersSubscription?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    this.subscriptionList.unsubscribe();
  }

  private subscribeToFiltersState() {
    this.subFiltersState$ = this._filtersStore
      .select(selectFilters)
      .pipe(distinctUntilChanged((prev, current) => prev === current))
      .subscribe({
        next: response => {
          if (response?.customer_filters) {
            this.customerFilters = this.configureCustomerFilterOptions(response.customer_filters);
            this._filterService.publishFilters(this.customerFilters);
            this.convertFiltersToParams(this.customerFilters);
          }
        }
      });
    this.subscriptionList.add(this.subFiltersState$);
  }

  private configureCustomerFilterOptions(filters: FilterItem[]): FilterItem[] {
    const localFilters: FilterItem[] = filters.map(filter => JSON.parse(JSON.stringify(filter)));

    localFilters.forEach(filter => {
      if (filter.id === 'filter_customer_search') {
        this.searchTerm = filter.value || '';
      } else {
        this.deselectAll(filter);
      }
    });

    this.setFiltersCounter(localFilters);
    return localFilters;
  }

  private deselectAll(filter: FilterItem) {
    if (!filter.options) return;
    filter.options.forEach(option => {
      if (option.value !== 'all' && option.selected) {
        const allOption = filter.options.find(el => el.value === 'all');
        if (allOption) allOption.selected = false;
      }
    });
  }

  private setFiltersCounter(filters: FilterItem[]) {
    this.filtersCounter = 0;
    filters.forEach(filter => {
      if (filter.options_selected > 0) {
        this.filtersCounter += 1;
      }
    });
  }

  private convertFiltersToParams(filters: FilterItem[]) {
    const page = Number(this._route.snapshot.queryParamMap.get('page')) || 1;
    this.getCustomers(this.buildQueryParams(filters, page));
  }

  private buildQueryParams(filters: FilterItem[], page: number): GetCustomerParams {
    const params: GetCustomerParams = {
      limit: 10,
      page
    };

    if (this.isArchivedFilter !== undefined) {
      params.isArchived = this.isArchivedFilter;
    }

    if (this.searchTerm) {
      params.title = this.searchTerm.trim();
    }

    if (this.selectedSort) {
      params.sort = this.selectedSort;
    }

    filters.forEach(filter => {
      const selectedOptions: string[] = [];

      switch (filter.id) {
        case 'filter_customer_status':
          filter.options?.forEach(option => {
            if (option.selected && option.value !== 'all') {
              selectedOptions.push(option.value);
            }
          });
          if (selectedOptions.length) {
            params.status = selectedOptions.join(',');
          }
          break;

        case 'filter_customer_type':
          filter.options?.forEach(option => {
            if (option.selected && option.value !== 'all') {
              selectedOptions.push(option.value);
            }
          });
          if (selectedOptions.length) {
            params.type = selectedOptions.join(',');
          }
          break;

        case 'filter_customer_acquisition':
          filter.options?.forEach(option => {
            if (option.selected && option.value !== 'all') {
              selectedOptions.push(option.value);
            }
          });
          if (selectedOptions.length) {
            params.acquisition = selectedOptions.join(',');
          }
          break;
      }
    });

    return params;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSearchInput(event: any) {
    const { value } = event.detail[this.searchModelKey];
    this.searchSubject.next(value);
  }

  private updateSearchFilter(term: string) {
    const localFilters: FilterItem[] = this.customerFilters.map(f => JSON.parse(JSON.stringify(f)));
    const searchFilter = localFilters.find(f => f.id === 'filter_customer_search');
    if (searchFilter) {
      searchFilter.value = term;
    }
    this._filtersStore.dispatch(filterActions.setCustomerFilters({ customer_filters: localFilters }));
  }

  onSortChange() {
    const localFilters: FilterItem[] = this.customerFilters.map(f => JSON.parse(JSON.stringify(f)));
    this.convertFiltersToParams(localFilters);
  }

  toggleFiltersPanel() {
    this.filtersActive = !this.filtersActive;
    this._filterService.toggleFilters(this.filtersActive);
  }

  getCustomers(queryParams?: GetCustomerParams) {
    this.getCustomersSubscription?.unsubscribe();
    this.isLoading = true;
    this.getCustomersSubscription = this._customerService
      .getCustomers(queryParams)
      .pipe(
        take(1),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: result => {
          this.customers = result.data;
          this.pagination = {
            page: result.page,
            pageCount: result.pageCount
          };
          this._filterService.setTotalRecords(result.total || result.data?.length || 0);
        },
        error: () => {
          this._notificationsService.openSnackBar({
            message: 'Error loading customers. Please try again.',
            action: 'Close',
            type: NotificationTypes.ERROR
          });
          this.customers = [];
        }
      });
  }

  async onPageChange(pageEvent: CustomEvent<{ pageIndex: number; prevPage: number }>) {
    await this._router.navigate([], {
      relativeTo: this._route,
      queryParams: {
        page: pageEvent.detail.pageIndex
      },
      queryParamsHandling: 'merge',
      replaceUrl: false
    });

    const localFilters: FilterItem[] = this.customerFilters.map(f => JSON.parse(JSON.stringify(f)));
    this.getCustomers(this.buildQueryParams(localFilters, pageEvent.detail.pageIndex));
  }
}
